import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import 'mapbox-gl/dist/mapbox-gl.css';

// props-types is a library for typechecking of props
import PropTypes from "prop-types";
import { useNavigate } from 'react-router-dom';
import { setGroupFeatures, setDeviceFeatures } from 'utils/map';
import { useSoftUIController } from 'hooks/context';
import moment from 'moment';

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function ClearwaysSubgroupsMap({ map, user, groups_data, setSelectedDevice }) {

    const navigate = useNavigate();
    const [controller] = useSoftUIController();
    const { deviceLayout, mapDevices, userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;
    const popupRef = useRef(new mapboxgl.Popup({ closeButton: false, closeOnClick: false }));

    const handleSubgroupClick = (e) => {
        console.log('handleSubgroupClick')
        e.preventDefault();
        popupRef.current.remove();
        navigate("/overview/" + e.features[0].properties.parent_id + "/" + e.features[0].properties.group_id);
    }

    const handleGroupClick = (e) => {
        if (!e.defaultPrevented) {
            e.preventDefault();
            popupRef.current.remove();
            navigate("/overview/" + e.features[0].properties.group_id);
        }
    }

    const handleDeviceClick = (e) => {
        e.preventDefault();
        popupRef.current.remove();
        var path = "/devices/" + e.features[0].properties.uuid;

        const browserEvent = e.originalEvent;
        if (browserEvent && (browserEvent.ctrlKey || browserEvent.metaKey)) {
            window.open(path, '_blank');
        }
        else {
            navigate(path);
        }
    };

    const handleMapClick = (e) => {
        if (!e.defaultPrevented) {
            popupRef.current.remove();
            navigate("/overview");
        }
    }

    const createPopup = (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        var description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b></div>`;

        if (showAdvancedDeviceLevel) {
            description = `<div style='text-align:center; font-size:16px'>
                        <div style='display: flex; justify-content: center;'>
                        <b>${e.features[0].properties.site_id}</b><span style='font-size:13px; margin-left: 3px;'>(${e.features[0].properties.name})</span>
                        </div>
                        <div style='display: flex; justify-content: center; align-items: center; margin-top: 5px;'>
                        <div style='color: ${e.features[0].properties.feature_colour}; margin-right: 5px;'>
                            <svg style='margin-top: 3px;' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="2" y="6" width="18" height="12" rx="2" ry="2" />
                            <line x1="23" y1="10" x2="23" y2="14" />
                            <rect x="5" y="9" width="10" height="6" rx="1" fill="currentColor" stroke="none" />
                            </svg>
                        </div>
                        <span style='font-size:13px'>${e.features[0].properties.battery}%</span>
                        </div>
                    </div>`;
        }

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        popupRef.current.setLngLat(coordinates).setHTML(description).addTo(map.current);

        // Only set the selected device if the menu is NOT open
        if (mapDevices && setSelectedDevice) {
            const parsedProps = { ...e.features[0].properties };
            for (let key in parsedProps) {
                try {
                    if (typeof parsedProps[key] === 'string' && (parsedProps[key].startsWith('{') || parsedProps[key].startsWith('['))) {
                        parsedProps[key] = JSON.parse(parsedProps[key]);
                    }
                } catch (err) { }
            }
            setSelectedDevice(parsedProps);
        }
    }

    const handleSubgroupMouseEnter = (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
    }

    const handleSubgroupMouseLeave = (e) => {
        map.current.getCanvas().style.cursor = '';
    }

    const handleDeviceMouseEnter = (e) => {
        if (!map.current) return;
        map.current.getCanvas().style.cursor = 'pointer';

        if (deviceLayout != "mobile") {
            createPopup(e);
        }
    }

    const handleDeviceMouseLeave = (e) => {
        map.current.getCanvas().style.cursor = '';
        popupRef.current.remove();
    }

    useEffect(() => { //mount
        if (!map.current) return;

        var existing_device_source = map.current.getSource('devices-source');
        var existing_device_layer = map.current.getLayer('devices-layer');

        if (!existing_device_source) {
            map.current.addSource('devices-source', {
                type: 'geojson',
                data: {
                    "type": "FeatureCollection",
                    "features": groups_data.devices.map(device => setDeviceFeatures(device, showAdvancedDeviceLevel)).sort((a, b) => { return a.properties.zIndex - b.properties.zIndex })
                }
            });
        }

        if (!existing_device_layer) {
            map.current.addLayer({
                id: 'devices-layer',
                type: 'circle',
                source: 'devices-source',
                paint: {
                    'circle-color': ['get', 'feature_colour'],
                    'circle-radius': 9,
                    'circle-stroke-width': 3,
                    'circle-stroke-color': '#ffffff'
                }
            });
        }

        var existing_base_layer = map.current.getLayer('groups-layer');
        var existing_base_source = map.current.getSource('groups-source');

        if (!existing_base_source) {
            map.current.addSource('groups-source', {
                type: 'geojson',
                data: {
                    "type": "FeatureCollection",
                    "features": setGroupFeatures(groups_data, showAdvancedDeviceLevel)
                }
            });
        }
        if (!existing_base_layer) {
            map.current.addLayer({
                id: 'groups-layer',
                type: 'fill',
                source: 'groups-source',
                paint: {
                    "fill-opacity": 0.1,
                    "fill-color": "white"
                }
            }).addLayer({
                id: 'groups-layer-liner',
                type: 'line',
                source: 'groups-source',
                paint: {
                    "line-color": "white",
                    "line-width": 2,
                    "line-opacity": 0.2
                }
            });
        }

        if (groups_data.subgroups.length > 0) {
            var existing_group_source = map.current.getSource('subgroups-source');
            var existing_group_layer = map.current.getLayer('subgroups-layer');

            if (!existing_group_source) {
                map.current.addSource('subgroups-source', {
                    type: 'geojson',
                    data: {
                        "type": "FeatureCollection",
                        "features": groups_data.subgroups.flatMap(subgroup => setGroupFeatures(subgroup, showAdvancedDeviceLevel))
                    }
                });
            }

            if (!existing_group_layer) {
                map.current.addLayer({
                    id: 'subgroups-layer',
                    type: 'line',
                    source: 'subgroups-source',
                    paint: {
                        "line-color": ['get', 'feature_colour'],
                        "line-width": 5,
                    }
                });
            }

            map.current.on('click', 'subgroups-layer', handleSubgroupClick);
            map.current.on('mouseenter', 'subgroups-layer', handleSubgroupMouseEnter);
            map.current.on('mouseleave', 'subgroups-layer', handleSubgroupMouseLeave);
        }

        map.current.on('click', 'groups-layer', handleGroupClick);

        map.current.on('click', 'devices-layer', handleDeviceClick);
        map.current.on('mouseenter', 'devices-layer', handleDeviceMouseEnter);
        map.current.on('mouseleave', 'devices-layer', handleDeviceMouseLeave);

        map.current.on('click', handleMapClick);

        return () => {
            map.current.off('click', 'subgroups-layer', handleSubgroupClick);
            map.current.off('mouseenter', 'subgroups-layer', handleSubgroupMouseEnter);
            map.current.off('mouseleave', 'subgroups-layer', handleSubgroupMouseLeave);

            map.current.off('click', 'groups-layer', handleGroupClick);

            map.current.off('click', 'devices-layer', handleDeviceClick);
            map.current.off('mouseenter', 'devices-layer', handleDeviceMouseEnter);
            map.current.off('mouseleave', 'devices-layer', handleDeviceMouseLeave);

            map.current.off('click', handleMapClick);
        }

    }, [mapDevices, groups_data]);

    useEffect(() => {
        if (!map.current) return;

        map.current.setFilter('devices-layer', mapDevices ? ['all'] : ['==', ['id'], '']);
        map.current.setFilter('subgroups-layer', !mapDevices ? ['all'] : ['==', ['id'], '']);

    }, [mapDevices]);

    useEffect(() => () => { //unmount
        if (groups_data.subgroups.length > 0) {
            map.current.removeLayer('subgroups-layer');
            map.current.removeSource('subgroups-source');
        }

        map.current.removeLayer('groups-layer-liner');
        map.current.removeLayer('groups-layer');
        map.current.removeSource('groups-source');

        map.current.removeLayer('devices-layer');
        map.current.removeSource('devices-source');

        map.current.getCanvas().style.cursor = '';
        popupRef.current.remove();

    }, []);
}

ClearwaysSubgroupsMap.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    groups_data: PropTypes.array,
};

export default ClearwaysSubgroupsMap;